/**
 * Pure helpers for the Meetings interactions (spec 11–14): highlighting search hits (⌘K, §3.4 row 4),
 * turning a text selection into a follow (row 3), and the Live now headline (row 1).
 */
import type { FollowType, LiveNow, Person } from "../types";

/** A record number: "SO2026-0025407", "O2026-0029055", "R2026-0001234", "Or2026-…". */
export const RECORD_RE = /\b(?:SO|O|R|Or|F|A)\d{4}-\d{4,8}\b/i;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Split `text` into plain and highlighted runs for the words of `q` (case-insensitive, prefix match:
 * "parking meter" marks "Parking", "parking meters"). Adjacent hits merge into one run.
 */
export function highlightParts(text: string, q: string): { text: string; hit: boolean }[] {
  const words = q.toLowerCase().split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}'-]/gu, "")).filter((w) => w.length > 1);
  if (!text || !words.length) return [{ text, hit: false }];
  const word = `(?:${words.map(escapeRe).join("|")})[\\p{L}\\p{N}'’]*`;
  const re = new RegExp(`(?<![\\p{L}\\p{N}])${word}(?:\\s+${word})*`, "giu");
  const out: { text: string; hit: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: text.slice(last, i), hit: false });
    out.push({ text: m[0], hit: true });
    last = i + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), hit: false });
  return out.length ? out : [{ text, hit: false }];
}

export interface FollowDraft { type: FollowType; refId?: string; keyword?: string; label: string; quoted: string }

const tidy = (s: string) => s.replace(/[“”"‘’]/g, "").replace(/\s+/g, " ").replace(/^[\s,.;:–—-]+|[\s,.;:–—-]+$/g, "").trim();
const lastName = (name: string) => name.replace(/\b(Jr|Sr|II|III)\.?$/i, "").trim().split(/\s+/).pop()?.toLowerCase() ?? "";

/**
 * What a selection should follow (spec 11 card 3: "+ Follow {type} '{text}'"):
 * a record number or "… ordinance/resolution" → legislation, a known person's name → person,
 * "Committee on …" → committee, anything else → a topic keyword.
 */
export function followFromSelection(raw: string, people: Record<string, Person> = {}): FollowDraft | null {
  const text = tidy(raw).slice(0, 80);
  if (text.length < 2) return null;
  const record = text.match(RECORD_RE)?.[0];
  if (record) return { type: "legislation", refId: record.toUpperCase(), label: record.toUpperCase(), quoted: text };
  const lower = text.toLowerCase().replace(/^(ald\.|alderperson|alderman|alderwoman)\s+/, "");
  const person = Object.values(people).find((p) => {
    const n = p.name.toLowerCase();
    return n === lower || (lower.length > 3 && (n.includes(lower) || lastName(p.name) === lower));
  });
  if (person) return { type: "person", refId: person.id, label: person.name, quoted: text };
  if (/^committee on\b/i.test(text)) return { type: "committee", refId: text, label: text, quoted: text };
  if (/\b(ordinance|resolution|order)\b/i.test(text)) return { type: "legislation", label: text, quoted: text };
  const label = text.charAt(0).toUpperCase() + text.slice(1);
  return { type: "topic", keyword: text.toLowerCase(), label, quoted: text };
}

/** Is this (type, ref/keyword) already followed? Topic follows compare keywords and labels case-insensitively. */
export function isFollowed(follows: { type: FollowType; refId?: string; keyword?: string; label: string }[], f: { type: FollowType; refId?: string; keyword?: string; label: string }) {
  const k = (s?: string) => (s ?? "").replace(/[“”"]/g, "").trim().toLowerCase();
  return follows.some((x) => x.type === f.type && (
    (f.refId && x.refId && k(x.refId) === k(f.refId)) ||
    (!f.refId && (k(x.keyword) || k(x.label)) === (k(f.keyword) || k(f.label)))
  ));
}

/** Banner title (spec 11 card 1): "{Match} is being discussed now"; a followed person: "{label} is speaking now". */
export const liveHeadline = (live: Pick<LiveNow, "headline" | "label" | "type">) =>
  live.headline ?? `${live.label} is ${live.type === "person" ? "speaking" : "being discussed"} now`;
